import { KeyRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/cn';
import { CopyIconButton } from '../ui';
import { ACCESS_LABEL, ACCESS_TILE, WifiTile } from './WifiTile';

interface Props {
  buildingCode: string | null;
  keyCode: string | null;
  keyCodeAvailableFrom: string | null;
  wifiSsid: string | null;
  wifiPassword: string | null;
}

/**
 * Zugang im Jetzt-Bereich: Haustür- und Wohnungscode, sonst ihre Freigabezeit, dazu das WLAN. Die
 * Codes gibt der Server erst in seinem Fenster heraus — hier steht nur, was er geliefert hat; das
 * WLAN hat keine Sperre. Ohne Code und ohne Freigabezeit hat das Objekt keinen Code: dann steht
 * hier nichts statt „wird vor Anreise mitgeteilt“ — der Satz war am dritten Aufenthaltstag falsch.
 */
export function AccessTiles(props: Props) {
  const { t } = useTranslation();
  const codes = [
    props.buildingCode ? { label: t('travel.access.building'), value: props.buildingCode } : null,
    props.keyCode ? { label: t('travel.access.apartment'), value: props.keyCode } : null,
  ].filter((code): code is { label: string; value: string } => code !== null);
  const showWifi = !!(props.wifiSsid || props.wifiPassword);
  const pending = !props.keyCode && !!props.keyCodeAvailableFrom;
  // Längere Codes bekommen je eine eigene Zeile, statt nebeneinander umzubrechen.
  const longCodes = codes.some((code) => code.value.length > 6);
  if (codes.length === 0 && !pending && !showWifi) return null;

  return (
    <div className="flex flex-col gap-2">
      {codes.length > 0 && (
        <div className={cn('grid gap-2', codes.length > 1 && !longCodes && 'grid-cols-2')}>
          {codes.map((code) => (
            <CodeTile key={code.label} {...code} />
          ))}
        </div>
      )}
      {pending && <PendingCodes availableFrom={props.keyCodeAvailableFrom!} />}
      {showWifi && <WifiTile ssid={props.wifiSsid} password={props.wifiPassword} />}
    </div>
  );
}

function CodeTile({ label, value }: { label: string; value: string }) {
  const { t } = useTranslation();
  // Kopierknopf in der Kopfzeile, der Code darunter über die ganze Breite: bei 360 px stand er
  // neben dem Knopf nur noch als „441…“ da — genau die Zahl, für die der Gast herkommt.
  return (
    <div className={cn(ACCESS_TILE, 'flex min-w-0 flex-col gap-0.5')}>
      <div className="flex items-center justify-between gap-2">
        <span className={ACCESS_LABEL}>{label}</span>
        <CopyIconButton
          value={value}
          label={t('travel.access.codeLabel', { label })}
          tone="dark"
          className="-my-1.5 -mr-1.5"
        />
      </div>
      <span className="break-all font-mono text-2xl font-semibold tracking-[0.12em] text-white">
        {value}
      </span>
    </div>
  );
}

/** Die Codes sind noch gesperrt: die echte Freigabezeit des Servers. */
function PendingCodes({ availableFrom }: { availableFrom: string }) {
  const { t, i18n } = useTranslation();
  const at = new Date(availableFrom);
  const locale = i18n.resolvedLanguage ?? 'de';
  const text = Number.isNaN(at.getTime())
    ? t('stations.stay.arrival.lockedHintNoTime')
    : t('travel.access.lockedUntil', {
        date: at.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' }),
        time: at.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }),
      });
  return (
    <p className={cn(ACCESS_TILE, 'flex items-start gap-2.5 text-sm text-white/85')}>
      <KeyRound className="text-forest-on-dark mt-0.5 size-4 shrink-0" aria-hidden />
      <span>
        <span className="block font-semibold text-white">{t('travel.access.codesTitle')}</span>
        {text}
      </span>
    </p>
  );
}
